import { Controller } from "@hotwired/stimulus"

// Passkeys: signing in with one on the sign-in page, adding one on the account page. The server sends the options
// (PasskeySignInsController, Accounts::PasskeysController), the browser has the phone or computer make or use the key,
// and the answer goes back to be checked. Hidden where the browser can't do passkeys.
export default class extends Controller {
  static targets = ["error"]
  static values = { options: String, url: String }

  connect() {
    this.element.hidden = !window.PublicKeyCredential
  }

  async signIn(event) {
    await this.#run(event, async (options) => {
      const publicKey = { ...options, challenge: bytes(options.challenge) }
      publicKey.allowCredentials = (options.allowCredentials || []).map((c) => ({ ...c, id: bytes(c.id) }))
      const key = await navigator.credentials.get({ publicKey })
      const r = key.response
      return {
        ...credential(key),
        response: {
          clientDataJSON: text(r.clientDataJSON), authenticatorData: text(r.authenticatorData),
          signature: text(r.signature), userHandle: r.userHandle && text(r.userHandle)
        }
      }
    })
  }

  async add(event) {
    await this.#run(event, async (options) => {
      const publicKey = { ...options, challenge: bytes(options.challenge), user: { ...options.user, id: bytes(options.user.id) } }
      publicKey.excludeCredentials = (options.excludeCredentials || []).map((c) => ({ ...c, id: bytes(c.id) }))
      const key = await navigator.credentials.create({ publicKey })
      const r = key.response
      return {
        ...credential(key),
        response: {
          clientDataJSON: text(r.clientDataJSON), attestationObject: text(r.attestationObject),
          transports: r.getTransports?.() || []
        }
      }
    })
  }

  async #run(event, make) {
    const button = event.currentTarget
    button.disabled = true
    this.#say("")
    try {
      const options = await post(this.optionsValue)
      if (options.error) return this.#say(options.error)
      const answer = await post(this.urlValue, { credential: await make(options) })
      if (answer.to) window.location.assign(answer.to)
      else this.#say(answer.error)
    } catch (e) {
      // Closing the phone's prompt is a choice, not a fault.
      this.#say(e?.name === "NotAllowedError" ? "" : "That passkey didn’t work. Try again, or use your email.")
    } finally {
      button.disabled = false
    }
  }

  #say(words) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = words
    this.errorTarget.hidden = !words
  }
}

async function post(url, body = {}) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json", Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    },
    body: JSON.stringify(body)
  })
  return res.json()
}

const credential = (key) => ({
  type: key.type, id: key.id, rawId: text(key.rawId),
  authenticatorAttachment: key.authenticatorAttachment, clientExtensionResults: key.getClientExtensionResults()
})

// The server speaks base64url; the browser wants bytes.
function bytes(s) {
  const b = atob(s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "="))
  return Uint8Array.from(b, (c) => c.charCodeAt(0))
}

function text(buffer) {
  let s = ""
  for (const b of new Uint8Array(buffer)) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}
